import { type ReactNode } from 'react'
import { Head, Link } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ArrowLeft, ExternalLink, FileText, Pencil } from 'lucide-react'
import type {
  ResearchCommitteeMemberRow,
  ResearchCustomFieldDef,
  ResearchEventDetail,
} from '@/types/research'

type Props = {
  event: ResearchEventDetail & { id: string; research_venue_id: string | null; archived: boolean }
  organization_name: string
  custom_fields: ResearchCustomFieldDef[]
}

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return null
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

function formatMoney(value: string | null | undefined) {
  if (!value) return null
  const num = parseFloat(value)
  if (isNaN(num) || num === 0) return null
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

function formatTime(value: string | null | undefined) {
  if (!value) return null
  const [h, m] = value.split(':').map(Number)
  const d = new Date()
  d.setHours(h, m)
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

function Detail({ label, value }: { label: string; value: ReactNode }) {
  if (value === null || value === undefined || value === '') return null
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  )
}

function CommitteeCard({ title, members }: { title: string; members: ResearchCommitteeMemberRow[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {title} ({members.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {members.map((member, i) => {
          const meta = [member.title, member.industry].filter(Boolean).join(' · ')
          return (
            <div key={member.id ?? i}>
              {member.id ? (
                <Link
                  href={`/admin/research/committee_members/${member.id}`}
                  className="text-sm font-medium hover:underline"
                >
                  {member.name}
                </Link>
              ) : (
                <p className="text-sm font-medium">{member.name}</p>
              )}
              {meta && <p className="text-xs text-muted-foreground">{meta}</p>}
            </div>
          )
        })}
      </CardContent>
    </Card>
  )
}

export default function ResearchEventShow({ event, organization_name, custom_fields }: Props) {
  const location = [event.venue_name, event.venue_address, event.city, event.state, event.venue_zip]
    .filter(Boolean)
    .join(', ')
  const times = [formatTime(event.start_time), formatTime(event.end_time)].filter(Boolean).join(' – ')
  const sumSponsorField = (key: 'tickets' | 'tables') => {
    const total = event.sponsorships.reduce((acc, s) => acc + (parseInt(s[key], 10) || 0), 0)
    return total > 0 ? total.toLocaleString('en-US') : null
  }
  const totalTickets = sumSponsorField('tickets')
  const totalTables = sumSponsorField('tables')
  const visibleCustomFields = custom_fields.filter(field => {
    const value = event.custom_data?.[field.key]
    return Array.isArray(value) ? value.length > 0 : Boolean(value)
  })

  return (
    <>
      <Head title={`Research - ${event.name || 'Event'}`} />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3">
          <Link href="/admin/research/events">
            <ArrowLeft className="mr-1 size-4" />
            Back to events
          </Link>
        </Button>

        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-semibold tracking-tight">{event.name || 'Untitled'}</h2>
              {event.event_type && <Badge variant="secondary">{event.event_type}</Badge>}
              {event.status && <Badge variant="outline">{event.status}</Badge>}
              {event.archived && <Badge variant="destructive">Archived</Badge>}
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              <Link
                href={`/admin/research/organizations/${event.research_organization_id}`}
                className="text-primary hover:underline"
              >
                {organization_name}
              </Link>
              {formatDate(event.date) && <> · {formatDate(event.date)}</>}
              {times && <> · {times}</>}
            </p>
            {location && <p className="text-sm text-muted-foreground">{location}</p>}
          </div>
          <Button asChild>
            <Link href={`/admin/research/events/${event.id}/edit`}>
              <Pencil className="mr-2 size-4" />
              Edit
            </Link>
          </Button>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {event.about && <p className="text-sm">{event.about}</p>}
                <div className="grid grid-cols-3 gap-4">
                  <Detail
                    label="Venue"
                    value={
                      event.venue_name && event.research_venue_id ? (
                        <Link
                          href={`/admin/research/venues/${event.research_venue_id}`}
                          className="text-primary hover:underline"
                        >
                          {event.venue_name}
                        </Link>
                      ) : (
                        event.venue_name || null
                      )
                    }
                  />
                  <Detail
                    label="Location"
                    value={
                      [event.venue_address, event.city, event.state, event.venue_zip]
                        .filter(Boolean)
                        .join(', ') || null
                    }
                  />
                  <Detail label="Attire" value={event.attire} />
                  <Detail label="Season" value={event.season} />
                  <Detail label="Estimated Attendance" value={event.estimated_attendance || null} />
                  <Detail label="Emcee" value={event.emcee} />
                  <Detail label="Entertainment" value={event.entertainment} />
                  <Detail label="Special Guest" value={event.special_guest} />
                  <Detail label="DJ / Music" value={event.dj_music} />
                  <Detail label="Auctioneer" value={event.auctioneer} />
                  <Detail label="Auction" value={event.auction} />
                  <Detail label="Gift Bag" value={event.gift_bag} />
                  <Detail label="Photographer" value={event.photographer} />
                  <Detail label="Event Consultant" value={event.event_consultant} />
                  <Detail label="Email Service" value={event.email_service} />
                  <Detail label="Platform" value={event.platform} />
                  <Detail label="Organization Budget" value={event.organization_budget} />
                </div>
                {event.causes.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {event.causes.map(cause => (
                      <Badge key={cause} variant="secondary">{cause}</Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {event.honorees.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Honorees ({event.honorees.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Company</TableHead>
                        <TableHead>Professional Network</TableHead>
                        <TableHead>Award</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {event.honorees.map((honoree, i) => (
                        <TableRow key={honoree.id ?? i}>
                          <TableCell className="font-medium">{honoree.name}</TableCell>
                          <TableCell className="text-muted-foreground">{honoree.honoree_type || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{honoree.company || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{honoree.industry || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{honoree.award || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {event.sponsorships.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <CardTitle>Sponsors ({event.sponsorships.length})</CardTitle>
                    {formatMoney(event.total_sponsor_revenue) && (
                      <Badge variant="secondary">
                        Total Revenue: {formatMoney(event.total_sponsor_revenue)}
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company / Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Professional Network</TableHead>
                        <TableHead className="text-center">Tickets</TableHead>
                        <TableHead className="text-center">Tables</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {event.sponsorships.map((sponsorship, i) => (
                        <TableRow key={sponsorship.id ?? i}>
                          <TableCell className="font-medium">{sponsorship.sponsor_name}</TableCell>
                          <TableCell className="text-muted-foreground">{sponsorship.tier || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {sponsorship.sponsor_industry || '—'}
                          </TableCell>
                          <TableCell className="text-center">{sponsorship.tickets || '—'}</TableCell>
                          <TableCell className="text-center">{sponsorship.tables || '—'}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatMoney(sponsorship.amount) || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {event.sponsorship_packages.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Sponsorship Packages ({event.sponsorship_packages.length})</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {event.sponsorship_packages.map((pkg, i) => (
                    <div key={pkg.id ?? i} className="rounded-xl bg-muted/50 p-4">
                      <div className="flex items-baseline justify-between">
                        <p className="font-medium">{pkg.name}</p>
                        <p className="font-semibold">{formatMoney(pkg.price) || '—'}</p>
                      </div>
                      {(pkg.tickets || pkg.tables) && (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {[
                            pkg.tickets && `${pkg.tickets} tickets`,
                            pkg.tables && `${pkg.tables} tables`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      )}
                      {pkg.description && (
                        <p className="mt-1 text-sm whitespace-pre-line text-muted-foreground">
                          {pkg.description}
                        </p>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {event.ticket_tiers.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Additional Tickets ({event.ticket_tiers.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ticket</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead className="text-center"># Tickets</TableHead>
                        <TableHead>Benefits / Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {event.ticket_tiers.map((tier, i) => (
                        <TableRow key={tier.id ?? i}>
                          <TableCell className="font-medium">{tier.name}</TableCell>
                          <TableCell className="text-right">{formatMoney(tier.price) || '—'}</TableCell>
                          <TableCell className="text-center">{tier.quantity || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{tier.description || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}

            {event.journal_ads.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Journal Ads ({event.journal_ads.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ad</TableHead>
                        <TableHead>Journal Type</TableHead>
                        <TableHead className="text-right">Price</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {event.journal_ads.map((ad, i) => (
                        <TableRow key={ad.id ?? i}>
                          <TableCell className="font-medium">{ad.name}</TableCell>
                          <TableCell className="text-muted-foreground">{ad.journal_type || '—'}</TableCell>
                          <TableCell className="text-right">{formatMoney(ad.price) || '—'}</TableCell>
                          <TableCell className="text-muted-foreground">{ad.description || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="space-y-6 self-start">
            {event.event_image_url && (
              <img
                src={event.event_image_url}
                alt={event.name || 'Event'}
                className="w-full rounded-2xl object-cover ring-1 ring-black/5"
              />
            )}

            <Card>
              <CardHeader>
                <CardTitle>Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Detail label="Ticket Price" value={formatMoney(event.ticket_price)} />
                <Detail label="Fundraising Goal" value={formatMoney(event.goal)} />
                <Detail label="Ticket Tax Deduction" value={event.ticket_tax_deduction} />
                <Detail label="Sponsor Revenue" value={formatMoney(event.total_sponsor_revenue)} />
                <Detail label="Total Tickets" value={totalTickets} />
                <Detail label="Total Tables" value={totalTables} />
                <p className="border-t border-black/5 pt-3 text-xs text-muted-foreground">
                  Estimated amounts &amp; attendance
                </p>
              </CardContent>
            </Card>

            {event.gala_committee_members.length > 0 && (
              <CommitteeCard title="Gala Committee" members={event.gala_committee_members} />
            )}

            {event.host_committee_members.length > 0 && (
              <CommitteeCard title="Host Committee" members={event.host_committee_members} />
            )}

            {(event.contact_name || event.contact_title || event.contact_email || event.event_email) && (
              <Card>
                <CardHeader>
                  <CardTitle>Development Contact</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Detail label="Name" value={event.contact_name} />
                  <Detail label="Title" value={event.contact_title} />
                  <Detail
                    label="Email"
                    value={
                      event.contact_email ? (
                        <a
                          href={`mailto:${event.contact_email}`}
                          className="text-primary hover:underline"
                        >
                          {event.contact_email}
                        </a>
                      ) : null
                    }
                  />
                  <Detail
                    label="Event Email"
                    value={
                      event.event_email ? (
                        <a
                          href={`mailto:${event.event_email}`}
                          className="text-primary hover:underline"
                        >
                          {event.event_email}
                        </a>
                      ) : null
                    }
                  />
                </CardContent>
              </Card>
            )}

            {(event.event_link || event.event_photo_video_url || event.flyers.length > 0) && (
              <Card>
                <CardHeader>
                  <CardTitle>Links &amp; files</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {event.event_link && (
                    <a
                      href={event.event_link}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <ExternalLink className="size-4 shrink-0" />
                      Event page
                    </a>
                  )}
                  {event.event_photo_video_url && (
                    <a
                      href={event.event_photo_video_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <ExternalLink className="size-4 shrink-0" />
                      Photos / video
                    </a>
                  )}
                  {event.flyers.map(flyer => (
                    <a
                      key={flyer.id}
                      href={flyer.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <FileText className="size-4 shrink-0" />
                      <span className="truncate">{flyer.filename}</span>
                    </a>
                  ))}
                </CardContent>
              </Card>
            )}

            {visibleCustomFields.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Custom fields</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {visibleCustomFields.map(field => {
                    const value = event.custom_data[field.key]
                    return (
                      <Detail
                        key={field.id}
                        label={field.label}
                        value={Array.isArray(value) ? value.join(', ') : value}
                      />
                    )
                  })}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

ResearchEventShow.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
